import AOSpatialIndex from '../ao/AOSpatialIndex.js';
import Debug from '../utils/Debug.js';

/**
 * Manages face occlusion detection for cuboids.
 * Hides faces that are entirely contained by a neighboring cuboid's face.
 */
export default class FaceOcclusion {
  static _spatialIndex = null;
  static _world = null;
  static enabled = true;

  // Epsilon for floating point comparisons
  static EPSILON = 0.0001;

  /**
   * Face configuration for occlusion detection.
   * Maps face name to the plane it lies on and how to get bounds.
   */
  static FACE_CONFIGS = {
    top: {
      // Top face lies at Y = y + height
      getPlaneValue: (c) => c.y + c.height,
      // Neighbor's bottom must touch our top (neighbor.y === our top)
      getNeighborPlaneValue: (n) => n.y,
      // Bounds in the XZ plane
      getBounds: (c) => ({ minU: c.x, maxU: c.x + c.width, minV: c.z, maxV: c.z + c.depth }),
      getNeighborBounds: (n) => ({ minU: n.x, maxU: n.x + n.width, minV: n.z, maxV: n.z + n.depth }),
      // Which face of the neighbor is blocking us (for transparency check)
      neighborBlockingFace: 'bottom'
    },
    bottom: {
      getPlaneValue: (c) => c.y,
      getNeighborPlaneValue: (n) => n.y + n.height,
      getBounds: (c) => ({ minU: c.x, maxU: c.x + c.width, minV: c.z, maxV: c.z + c.depth }),
      getNeighborBounds: (n) => ({ minU: n.x, maxU: n.x + n.width, minV: n.z, maxV: n.z + n.depth }),
      neighborBlockingFace: 'top'
    },
    front: {
      // Front face lies at Z = z + depth (faces away from camera, toward +Z)
      // Since worldZ = -z, lower z = closer to camera
      // For occlusion: neighbor must be BETWEEN face and camera (at lower z)
      // So neighbor's front (n.z + n.depth) must touch our front
      getPlaneValue: (c) => c.z + c.depth,
      getNeighborPlaneValue: (n) => n.z + n.depth,
      // Bounds in the XY plane
      getBounds: (c) => ({ minU: c.x, maxU: c.x + c.width, minV: c.y, maxV: c.y + c.height }),
      getNeighborBounds: (n) => ({ minU: n.x, maxU: n.x + n.width, minV: n.y, maxV: n.y + n.height }),
      neighborBlockingFace: 'front'
    },
    back: {
      // Back face lies at Z = z (faces toward camera, toward -Z)
      // For occlusion: neighbor's back must be at same plane (co-planar backs)
      // So neighbor.z === our z (both backs at same position, facing same direction)
      getPlaneValue: (c) => c.z,
      getNeighborPlaneValue: (n) => n.z,
      getBounds: (c) => ({ minU: c.x, maxU: c.x + c.width, minV: c.y, maxV: c.y + c.height }),
      getNeighborBounds: (n) => ({ minU: n.x, maxU: n.x + n.width, minV: n.y, maxV: n.y + n.height }),
      neighborBlockingFace: 'back'
    },
    right: {
      // Right face lies at X = x + width
      getPlaneValue: (c) => c.x + c.width,
      // Neighbor's left must touch our right (neighbor.x === our right)
      getNeighborPlaneValue: (n) => n.x,
      // Bounds in the YZ plane
      getBounds: (c) => ({ minU: c.y, maxU: c.y + c.height, minV: c.z, maxV: c.z + c.depth }),
      getNeighborBounds: (n) => ({ minU: n.y, maxU: n.y + n.height, minV: n.z, maxV: n.z + n.depth }),
      neighborBlockingFace: 'left'
    },
    left: {
      getPlaneValue: (c) => c.x,
      getNeighborPlaneValue: (n) => n.x + n.width,
      getBounds: (c) => ({ minU: c.y, maxU: c.y + c.height, minV: c.z, maxV: c.z + c.depth }),
      getNeighborBounds: (n) => ({ minU: n.y, maxU: n.y + n.height, minV: n.z, maxV: n.z + n.depth }),
      neighborBlockingFace: 'right'
    }
  };

  /**
   * Initialize the face occlusion system.
   * @param {World} world - The world instance
   */
  static initialize(world) {
    this._world = world;
    // Use cell size of 2 for efficient neighbor queries
    this._spatialIndex = new AOSpatialIndex(2);
    // Build initial index from existing cuboids
    this._spatialIndex.rebuild(world.cuboids);
  }

  /**
   * Add a cuboid to the spatial index.
   * @param {Cuboid} cuboid - The cuboid to add
   */
  static addToIndex(cuboid) {
    if (!this._spatialIndex) return;
    this._spatialIndex.insert(cuboid);
  }

  /**
   * Remove a cuboid from the spatial index.
   * @param {Cuboid} cuboid - The cuboid to remove
   */
  static removeFromIndex(cuboid) {
    if (!this._spatialIndex) return;
    this._spatialIndex.remove(cuboid.getKey());
  }

  /**
   * Check if a face's bounds are fully contained by another bounds.
   * @param {Object} face - {minU, maxU, minV, maxV}
   * @param {Object} neighbor - {minU, maxU, minV, maxV}
   * @returns {boolean} True if face is fully contained by neighbor
   */
  static isFullyContained(face, neighbor) {
    return (
      neighbor.minU <= face.minU + this.EPSILON &&
      neighbor.maxU >= face.maxU - this.EPSILON &&
      neighbor.minV <= face.minV + this.EPSILON &&
      neighbor.maxV >= face.maxV - this.EPSILON
    );
  }

  /**
   * Check if a material is fully opaque (not transparent).
   * @param {Material} material - The material to check
   * @returns {boolean} True if fully opaque
   */
  static isMaterialOpaque(material) {
    if (!material) return true;
    // Check explicit opacity
    if (material.opacity < 1.0) return false;
    // Check glass effect
    if (material.effectType === 'glass') return false;
    return true;
  }

  /**
   * Check if a cuboid's face is fully opaque, considering MaterialGroup opacity.
   * @param {Cuboid} cuboid - The cuboid to check
   * @param {string} faceName - The face name
   * @returns {boolean} True if fully opaque
   */
  static isFaceOpaque(cuboid, faceName) {
    // First check the material directly on the face
    if (!this.isMaterialOpaque(cuboid.materials[faceName])) {
      return false;
    }

    // Check if face belongs to a MaterialGroup with transparency
    if (this._world && this._world.materialGroupManager) {
      const group = this._world.materialGroupManager.getGroupForFace(cuboid.getKey(), faceName);
      if (group && group.material) {
        // Check group's material opacity
        if (group.material.opacity < 1.0) {
          return false;
        }
        // Check group's colorOpacity (only applies to color materials)
        if (group.material.type === 'color' && group.colorOpacity < 1.0) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get neighboring cuboids that could potentially occlude faces.
   * @param {Cuboid} cuboid - The cuboid to find neighbors for
   * @returns {Set<Cuboid>} Set of neighboring cuboids
   */
  static getNeighbors(cuboid) {
    if (!this._spatialIndex) return new Set();

    // Query a slightly expanded AABB to find adjacent cuboids
    const neighbors = this._spatialIndex.queryAABB(
      cuboid.x - 1,
      cuboid.y - 1,
      cuboid.z - 1,
      cuboid.x + cuboid.width + 1,
      cuboid.y + cuboid.height + 1,
      cuboid.z + cuboid.depth + 1
    );

    // Remove self from results
    neighbors.delete(cuboid);

    return neighbors;
  }

  // Debug flag - set to true to log occlusion decisions
  static debug = false;

  /**
   * Calculate which faces of a cuboid are occluded by neighbors.
   * @param {Cuboid} cuboid - The cuboid to check
   * @returns {Set<string>} Set of occluded face names
   */
  static calculateOccludedFaces(cuboid) {
    const occluded = new Set();

    if (!this.enabled || !this._spatialIndex) {
      return occluded;
    }

    // Skip fragments - they inherit occlusion from parent
    if (cuboid.isFragment) {
      return occluded;
    }

    // Skip collider-only cuboids — they have no visible faces
    if (cuboid._colliderOnly) {
      return occluded;
    }

    const neighbors = this.getNeighbors(cuboid);
    const cuboidKey = `(${cuboid.x},${cuboid.y},${cuboid.z}) ${cuboid.width}x${cuboid.height}x${cuboid.depth}`;

    // Check each face
    for (const [faceName, config] of Object.entries(this.FACE_CONFIGS)) {
      // Skip if face material is transparent (including MaterialGroup opacity)
      if (!this.isFaceOpaque(cuboid, faceName)) {
        continue;
      }

      const planeValue = config.getPlaneValue(cuboid);
      const faceBounds = config.getBounds(cuboid);

      // Check if any neighbor fully contains this face
      for (const neighbor of neighbors) {
        // Skip fragments
        if (neighbor.isFragment) continue;
        // Skip collider-only cuboids (e.g., invisible ceilings) — they don't visually block
        if (neighbor._colliderOnly) continue;

        // Skip if neighbor's blocking face is transparent (we can see through it)
        // This includes checking MaterialGroup opacity
        const neighborBlockingFace = config.neighborBlockingFace;
        if (!this.isFaceOpaque(neighbor, neighborBlockingFace)) {
          continue;
        }

        const neighborPlane = config.getNeighborPlaneValue(neighbor);
        const neighborKey = `(${neighbor.x},${neighbor.y},${neighbor.z}) ${neighbor.width}x${neighbor.height}x${neighbor.depth}`;

        // Check if neighbor touches this face's plane
        if (Math.abs(neighborPlane - planeValue) < this.EPSILON) {
          const neighborBounds = config.getNeighborBounds(neighbor);
          const contained = this.isFullyContained(faceBounds, neighborBounds);

          if (this.debug) {
            Debug.log('FaceOcclusion', `${cuboidKey} ${faceName}: plane=${planeValue}, neighbor ${neighborKey} plane=${neighborPlane}`);
            Debug.log('FaceOcclusion', `  faceBounds:`, faceBounds);
            Debug.log('FaceOcclusion', `  neighborBounds:`, neighborBounds);
            Debug.log('FaceOcclusion', `  contained: ${contained}`);
          }

          // Check if neighbor fully contains this face
          if (contained) {
            if (this.debug) {
              Debug.log('FaceOcclusion', `  -> OCCLUDED by ${neighborKey}`);
            }
            occluded.add(faceName);
            break; // Face is occluded, no need to check more neighbors
          }
        }
      }
    }

    return occluded;
  }

  /**
   * Update occlusion for a single cuboid.
   * @param {Cuboid} cuboid - The cuboid to update
   * @returns {boolean} True if occlusion changed
   */
  static updateCuboid(cuboid) {
    if (!cuboid || cuboid.isFragment) return false;

    const newOccluded = this.calculateOccludedFaces(cuboid);
    const oldOccluded = cuboid.occludedFaces || new Set();

    // Check if occlusion changed
    const changed = !this.setsEqual(oldOccluded, newOccluded);

    if (changed) {
      cuboid.occludedFaces = newOccluded;
      // Rebuild face DOM elements
      if (typeof cuboid.rebuildFaces === 'function') {
        cuboid.rebuildFaces();
      }
    }

    return changed;
  }

  /**
   * Update occlusion for all neighbors of a cuboid.
   * Call this after adding/removing/moving a cuboid.
   * @param {Cuboid} cuboid - The cuboid whose neighbors should be updated
   */
  static updateNeighbors(cuboid) {
    if (!this.enabled || !this._spatialIndex) return;

    const neighbors = this.getNeighbors(cuboid);
    for (const neighbor of neighbors) {
      this.updateCuboid(neighbor);
    }
  }

  /**
   * Check if two sets are equal.
   * @param {Set} a - First set
   * @param {Set} b - Second set
   * @returns {boolean} True if sets are equal
   */
  static setsEqual(a, b) {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  /**
   * Rebuild spatial index from world cuboids.
   * Call this after loading a scene.
   */
  static rebuildIndex() {
    if (!this._world || !this._spatialIndex) return;
    this._spatialIndex.rebuild(this._world.cuboids);
  }

  /**
   * Update occlusion for all cuboids in the world.
   * Call this after loading a scene.
   */
  static updateAll() {
    if (!this._world) return;
    for (const cuboid of this._world.cuboids.values()) {
      this.updateCuboid(cuboid);
    }
  }
}
